import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Plus, Trash2, Save, RotateCcw } from 'lucide-react';
import { db } from '@/lib/firebase';
import { SEED_MENU, type MenuData } from '@/lib/useTreatments';
import type { Treatment, TreatmentCategory } from '@/data/treatments';

const uid = () => Math.random().toString(36).slice(2, 9);
const clone = (m: MenuData): MenuData => JSON.parse(JSON.stringify(m));

export function Treatments() {
  const [menu, setMenu] = useState<MenuData>(clone(SEED_MENU));
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'treatments', 'menu'));
        if (snap.exists()) {
          const d = snap.data() as Partial<MenuData>;
          setMenu({
            categories: d.categories?.length ? d.categories : SEED_MENU.categories,
            winterSpecials: d.winterSpecials ?? SEED_MENU.winterSpecials,
          });
        }
      } catch {
        /* keep seed */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3500); }

  function updateCat(ci: number, patch: Partial<TreatmentCategory>) {
    setMenu((m) => ({ ...m, categories: m.categories.map((c, i) => (i === ci ? { ...c, ...patch } : c)) }));
  }
  function updateItem(ci: number, ti: number, patch: Partial<Treatment>) {
    setMenu((m) => ({
      ...m,
      categories: m.categories.map((c, i) =>
        i === ci ? { ...c, treatments: c.treatments.map((t, j) => (j === ti ? { ...t, ...patch } : t)) } : c,
      ),
    }));
  }
  function addItem(ci: number) {
    setMenu((m) => ({
      ...m,
      categories: m.categories.map((c, i) =>
        i === ci ? { ...c, treatments: [...c.treatments, { id: uid(), name: 'New treatment', price: 0 }] } : c,
      ),
    }));
  }
  function removeItem(ci: number, ti: number) {
    setMenu((m) => ({
      ...m,
      categories: m.categories.map((c, i) =>
        i === ci ? { ...c, treatments: c.treatments.filter((_, j) => j !== ti) } : c,
      ),
    }));
  }
  function addCategory() {
    setMenu((m) => ({ ...m, categories: [...m.categories, { id: uid(), title: 'New category', blurb: '', treatments: [] }] }));
  }
  function removeCategory(ci: number) {
    setMenu((m) => ({ ...m, categories: m.categories.filter((_, i) => i !== ci) }));
  }
  function updateSpecial(si: number, patch: Partial<Treatment>) {
    setMenu((m) => ({ ...m, winterSpecials: m.winterSpecials.map((t, i) => (i === si ? { ...t, ...patch } : t)) }));
  }
  function addSpecial() {
    setMenu((m) => ({ ...m, winterSpecials: [...m.winterSpecials, { id: uid(), name: 'New special', price: 0 }] }));
  }
  function removeSpecial(si: number) {
    setMenu((m) => ({ ...m, winterSpecials: m.winterSpecials.filter((_, i) => i !== si) }));
  }

  async function save() {
    setBusy(true);
    try {
      await setDoc(doc(db, 'treatments', 'menu'), { ...menu, updatedAt: new Date().toISOString() });
      flash('Saved. The website menu is updated.');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <p className="font-body text-sm text-muted">Loading menu…</p>;

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-espresso">Treatments</h1>
          <p className="mt-1 font-body text-sm text-muted">Edit the live menu and prices. Changes appear on the website on save.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMenu(clone(SEED_MENU))} className="btn-outline !px-4 !py-2.5"><RotateCcw size={15} /> Reset to defaults</button>
          <button onClick={save} disabled={busy} className="btn-primary !px-5 !py-2.5"><Save size={15} /> {busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        {menu.categories.map((cat, ci) => (
          <section key={cat.id} className="border border-line bg-paper p-5">
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <input className="field !py-2 font-display !text-lg" value={cat.title} onChange={(e) => updateCat(ci, { title: e.target.value })} />
                <input className="field !py-2 !text-sm" value={cat.blurb} placeholder="Short description" onChange={(e) => updateCat(ci, { blurb: e.target.value })} />
              </div>
              <button onClick={() => removeCategory(ci)} className="mt-1 text-muted hover:text-blush-deep" aria-label="Remove category"><Trash2 size={16} /></button>
            </div>

            <div className="mt-4 space-y-2">
              {cat.treatments.map((t, ti) => (
                <div key={t.id} className="space-y-1.5 border-b border-line/60 pb-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <input className="field !py-2 flex-1" value={t.name} onChange={(e) => updateItem(ci, ti, { name: e.target.value })} placeholder="Treatment name" />
                    <input className="field !py-2 w-24" type="number" value={t.price} onChange={(e) => updateItem(ci, ti, { price: Number(e.target.value) })} placeholder="Price" />
                    <input className="field !py-2 w-20" type="number" value={t.durationMin ?? ''} onChange={(e) => updateItem(ci, ti, { durationMin: e.target.value ? Number(e.target.value) : undefined })} placeholder="min" />
                    <button onClick={() => removeItem(ci, ti)} className="text-muted hover:text-blush-deep" aria-label="Remove"><Trash2 size={15} /></button>
                  </div>
                  <input className="field !py-2 !text-sm" value={t.description ?? ''} onChange={(e) => updateItem(ci, ti, { description: e.target.value || undefined })} placeholder="Description (shown when a client taps the treatment)" />
                </div>
              ))}
              <button onClick={() => addItem(ci)} className="mt-1 inline-flex items-center gap-1.5 font-body text-sm text-gold-deep hover:underline"><Plus size={14} /> Add treatment</button>
            </div>
          </section>
        ))}

        <button onClick={addCategory} className="inline-flex items-center gap-2 border border-dashed border-line bg-paper px-5 py-3 font-body text-sm text-espresso hover:border-gold"><Plus size={16} /> Add category</button>

        <section className="border border-blush/50 bg-blush-soft/30 p-5">
          <h2 className="font-display text-xl text-espresso">Winter Specials</h2>
          <div className="mt-3 space-y-2">
            {menu.winterSpecials.map((t, si) => (
              <div key={t.id} className="flex items-center gap-2">
                <input className="field !py-2 flex-1" value={t.name} onChange={(e) => updateSpecial(si, { name: e.target.value })} />
                <input className="field !py-2 w-24" type="number" value={t.price} onChange={(e) => updateSpecial(si, { price: Number(e.target.value) })} />
                <button onClick={() => removeSpecial(si)} className="text-muted hover:text-blush-deep" aria-label="Remove"><Trash2 size={15} /></button>
              </div>
            ))}
            <button onClick={addSpecial} className="mt-1 inline-flex items-center gap-1.5 font-body text-sm text-blush-deep hover:underline"><Plus size={14} /> Add special</button>
          </div>
        </section>
      </div>

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
